import React, { useCallback, useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Typography, Image } from 'lib/components';
import { Checkbox } from 'lib/components/FormFields';
import InventoryRow from './InventoryRow';
import { useWindowResize } from 'hooks/useWindowResize';
import { Inventory } from 'types';
import { useSelectVendorName } from 'store';
import { HeaderItem, Sort, SortField } from './types';
import { inventoryNameCompareFn, inventoryStockCompareFn, inventoryVendorNameCompareFn } from './sort';
import classes from './index.module.scss';

interface Props {
	inventory: Inventory[];
	checkedId: number;
	onRowClick: (inventory: Inventory) => void;
	checkAction: (id: number, isChecked: boolean) => void;
}

const HEADER_ITEMS: HeaderItem[] = [
	{ label: 'Name', field: 'name', sort: 'asc' },
	{ label: 'Vendor Name', field: 'vendorName', sort: 'asc' },
	{ label: 'Stock', field: 'stock', sort: 'asc' },
	{ label: 'Item Cost (X1)' },
	{ label: 'Package Cost' },
	{ label: 'Modified' },
];

const InventoriesDataTable = ({ inventory, checkedId, checkAction, onRowClick }: Props) => {
	const { isSmallerDevice } = useWindowResize();
	const getVendorName = useSelectVendorName();
	const [allChecked, setAllChecked] = useState(false);
	const [sortFields] = useState(
		HEADER_ITEMS.filter((item) => item.field && item.sort).reduce<Record<string, Sort>>((acc, cur) => {
			if (cur.field && cur.sort) {
				acc[cur.field] = cur!.sort;
			}
			return acc;
		}, {})
	);
	const [currentSortField, setCurrentSortField] = useState<SortField>({
		sort: 'asc',
		field: 'name',
	});
	const [sortedInventory, setSortedInventory] = useState(inventory);

	const sortInventories = useCallback(
		(inventories: Inventory[]) => {
			const sorted = [...inventories].sort((a, b) => {
				switch (currentSortField?.sort) {
					case 'asc':
						if (currentSortField?.field === 'name') {
							return inventoryNameCompareFn(a, b, 'asc');
						} else if (currentSortField?.field === 'vendorName') {
							return inventoryVendorNameCompareFn(
								getVendorName(a.vendorId),
								getVendorName(b.vendorId),
								'asc'
							);
						} else if (currentSortField?.field === 'stock') {
							return inventoryStockCompareFn(a, b, 'asc');
						}

						return 0;

					case 'desc':
						if (currentSortField?.field === 'name') {
							return inventoryNameCompareFn(a, b, 'desc');
						} else if (currentSortField?.field === 'vendorName') {
							return inventoryVendorNameCompareFn(
								getVendorName(a.vendorId),
								getVendorName(b.vendorId),
								'desc'
							);
						} else if (currentSortField?.field === 'stock') {
							return inventoryStockCompareFn(a, b, 'desc');
						}
						return 0;

					default:
						return 0;
				}
			});
			return sorted;
		},
		[currentSortField]
	);

	useEffect(() => {
		setSortedInventory(sortInventories(inventory));
	}, [inventory, sortInventories]);

	useEffect(() => {
		const sorted = sortInventories(sortedInventory);

		setSortedInventory(sorted);
	}, [setSortedInventory, sortInventories]);

	const handleHeaderItemClick = useCallback(
		(headerItem: HeaderItem) => {
			if (headerItem.field && sortFields[headerItem.field]) {
				if (headerItem.field === currentSortField?.field) {
					setCurrentSortField((prev) => {
						return {
							...prev,
							sort: prev?.sort === 'asc' ? 'desc' : 'asc',
						};
					});
				} else if (currentSortField?.field !== headerItem.field) {
					setCurrentSortField({ field: headerItem.field, sort: 'desc' });
				}
			}
		},
		[currentSortField, sortFields, setCurrentSortField]
	);

	const renderHeaderItem = (headerItem: HeaderItem) => {
		if (!headerItem.field || !headerItem.sort) {
			return (
				<div key={Math.random()} className={classes['col']}>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					{/* <Image src={'icTableDown'} /> */}
				</div>
			);
		} else {
			return (
				<div
					onClick={() => handleHeaderItemClick(headerItem)}
					key={Math.random()}
					className={classes['col']}
					style={{
						cursor: 'pointer',
					}}
				>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					<Image
						src={'icTableDown'}
						style={{
							marginLeft: '0.5rem',
							transform:
								currentSortField?.field === headerItem.field && currentSortField.sort === 'desc'
									? 'rotate(180deg)'
									: 'initial',
						}}
					/>
				</div>
			);
		}
	};

	return (
		<div className={classes['inventories-datatable']}>
			<div className={classes['table-wrapper']}>
				<div className={classes['table-container']}>
					<div className={classes['toolbar']}>
						<Checkbox
							checked={allChecked}
							onChange={() => setAllChecked(!allChecked)}
							style={{
								margin: '0 2rem 0.1rem 2rem',
							}}
						/>
						{HEADER_ITEMS.map((item) => renderHeaderItem(item))}
					</div>
					<div className={classes['container']}>
						<Scrollbars
							style={{
								flex: 1,
							}}
						>
							{sortedInventory.map((item) => (
								<InventoryRow
									key={item.id}
									inventory={item}
									vendorName={getVendorName(item.vendorId) ?? ''}
									checkedId={checkedId}
									checkAction={checkAction}
									onRowClick={onRowClick}
								/>
							))}
						</Scrollbars>
					</div>
				</div>
			</div>
		</div>
	);
};

export default InventoriesDataTable;
